import React, { useState, useEffect } from 'react'
import {
  Form,
  Input,
  Button,
  Table,
  Space,
  Select,
  Col,
  Row,
  Modal,
} from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import AdminHeader from '../../CommonComponents/AdminHeader'
import AdminFooter from '../../CommonComponents/AdminFooter'
import {
  getBuilding,
  getFloor,
  saveFloor,
  deleteFloor,
  updateFloor,
} from '../../APIComponents/AdminApi'
import { toast } from 'react-toastify'
import axiosInstance from '../../../Utils/axiosInstance'

const FloorMaster = () => {
  //===============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find((p) => p.page_name === 'Floor Master')
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1
  //============================================

  const [form] = Form.useForm()
  const [floor, setFloor] = useState([])
  const [buildingList, setBuildingList] = useState([])
  const [loading, setLoading] = useState(false)
  const [addLoading, setAddLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoadingId, setDeleteLoadingId] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedDeleteId, setSelectedDeleteId] = useState(null)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [selectedFloor, setSelectedFloor] = useState(null)

  const handleView = (record) => {
    setSelectedFloor(record)
    setViewModalVisible(true)
  }

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  })

  //  Fetch all floor
  const fetchFloor = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.get(getFloor)
      setFloor(res.data)
    } catch (err) {
      toast.error('Failed to fetch Floor')
    } finally {
      setLoading(false)
    }
  }

  //  Fetch all Building for dropdown
  const fetchBuildingList = async () => {
    try {
      const res = await axiosInstance.get(getBuilding)
      setBuildingList(res.data)
    } catch (err) {
      toast.error('Failed to fetch Building list')
    }
  }

  useEffect(() => {
    fetchBuildingList()
    fetchFloor()
  }, [])

  //  Add or Update Floor
  const onFinish = async (values) => {
    const { buildingId, floorName } = values
    if (!buildingId || !floorName) {
      toast.error('Please fill in all required fields')
      return
    }

    const saveObj = {
      building_id: buildingId,
      wing_name: floorName,
    }
    const updateObj = {
      building_id: buildingId,
      wing_name: floorName,
    }

    try {
      if (editingId) {
        setUpdateLoading(true)
        const res = await axiosInstance.put(
          `${updateFloor}/${editingId}`,
          updateObj,
        )
        if (res.data === 'UPDATED') {
          toast.success('Floor updated successfully')
          fetchFloor()
        } else if (res.data === 'ALREADYEXIST') {
          toast.warn('Floor already exist')
          return
        }
      } else {
        setAddLoading(true)
        const res = await axiosInstance.post(saveFloor, saveObj)
        if (res.data === 'SAVED') {
          toast.success('Floor added successfully')
          fetchFloor()
        } else if (res.data === 'ALREADYEXIST') {
          toast.warn('Floor already exist')
          return
        }
      }
      form.resetFields()
      setEditingId(null)
    } catch (err) {
      toast.error('Operation failed. Please try again.')
    } finally {
      setAddLoading(false)
      setUpdateLoading(false)
    }
  }

  //  Edit Floor
  const handleEdit = (record) => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })

    setEditingId(record.id)
    form.setFieldsValue({
      buildingId: record.building_id,
      floorName: record.wing_name,
    })
  }

  //  Delete Floor
  const handleDelete = async () => {
    try {
      setDeleteLoadingId(selectedDeleteId)

      const res = await axiosInstance.delete(
        `${deleteFloor}/${selectedDeleteId}`,
      )

      if (res.data === 'DELETED') {
        toast.success('Floor deleted successfully')
        fetchFloor()
        setIsDeleteModalOpen(false)
        setSelectedDeleteId(null)
      }
    } catch (err) {
      toast.error('Failed to delete Floor')
    } finally {
      setDeleteLoadingId(null)
    }
  }

  //  Cancel Edit
  const handleCancel = () => {
    setEditingId(null)
    form.resetFields()
  }

  //  Table Columns
  const columns = [
    {
      title: 'Sr. No',
      key: 'index',
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
      width: '10%',
    },
    {
      title: 'Building Name',
      render: (_, record) => record.building?.building_name || '-',
      key: 'building_name',
    },
    {
      title: 'Floor Name',
      dataIndex: 'wing_name',
      key: 'wing_name',
    },
    ...(showActionColumn
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: '25%',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    title="Edit"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  ></Button>
                )}
                {pagePermission.is_delete == 1 && (
                  <Button
                    title="Delete"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setSelectedDeleteId(record.id)
                      setIsDeleteModalOpen(true)
                    }}
                  ></Button>
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <AdminHeader />

      <div
        className="student-form-section container-fluid"
        style={{ minHeight: '100vh' }}
      >
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-2">Floor Master</h3>
          </div>

          <div className="row student-form-container shadow p-4 rounded bg-white">
            {/* Floor Form */}
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              style={{ marginBottom: '20px' }}
            >
              <Row gutter={16} className="pt-3">
                {/* Building Dropdown */}
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Building"
                    name="buildingId"
                    rules={[
                      { required: true, message: 'Please select building' },
                    ]}
                  >
                    <Select
                      placeholder="Select Building"
                      showSearch
                      optionFilterProp="children"
                    >
                      {buildingList.map((c) => (
                        <Select.Option key={c.id} value={c.id}>
                          {c.building_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                {/* Floor Input */}
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Floor Name"
                    name="floorName"
                    rules={[
                      { required: true, message: 'Please enter Floor name' },
                    ]}
                  >
                    <Input placeholder="Enter Floor" />
                  </Form.Item>
                </Col>

                {/* Buttons */}
                <Col
                  xs={24}
                  sm={12}
                  md={8}
                  style={{ display: 'flex', alignItems: 'end' }}
                >
                  <Form.Item>
                    <Space>
                      {!editingId ? (
                        <>
                          {pagePermission.is_new == 1 && (
                            <Button
                              type="primary"
                              htmlType="submit"
                              icon={<PlusOutlined />}
                              loading={addLoading}
                            >
                              Add
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          <Button
                            type="primary"
                            htmlType="submit"
                            icon={<EditOutlined />}
                            loading={updateLoading}
                          >
                            Update
                          </Button>
                          <Button
                            onClick={handleCancel}
                            disabled={updateLoading}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {/* Floor Table */}
            <Table
              columns={columns}
              dataSource={floor}
              rowKey="id"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: floor.length,
                onChange: (page, pageSize) => {
                  setPagination({
                    current: page,
                    pageSize: pageSize,
                  })
                },
              }}
              bordered
              loading={loading}
              className="shadow rounded custom-table p-0"
              style={{ overflowX: 'auto', border: '1px solid #dfdfdf' }}
            />
          </div>

          {/* ======== Delete Modal ====== */}
          <Modal
            title="Delete Floor"
            open={isDeleteModalOpen}
            onCancel={() => {
              setIsDeleteModalOpen(false)
              setSelectedDeleteId(null)
            }}
            footer={[
              <Button
                key="cancel"
                onClick={() => {
                  setIsDeleteModalOpen(false)
                  setSelectedDeleteId(null)
                }}
              >
                Cancel
              </Button>,
              <Button
                key="delete"
                type="primary"
                danger
                loading={deleteLoadingId !== null}
                onClick={handleDelete}
              >
                Delete
              </Button>,
            ]}
          >
            <p>Are you sure you want to delete this Floor?</p>
          </Modal>

          {/* ======== View Modal ====== */}
          <Modal
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View Floor Details
              </div>
            }
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            centered
          >
            <div
              style={{
                padding: '15px',
                borderRadius: '8px',
                backgroundColor: '#f5f7fa',
                border: '1px solid #e6e6e6',
              }}
            >
              <Row gutter={[16, 16]}>
                {/* Building Name */}
                <Col span={8}>
                  <span style={{ fontWeight: 600 }}>Building Name :</span>
                </Col>
                <Col span={16}>
                  <div
                    style={{
                      backgroundColor: '#ffffff',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    {selectedFloor?.building?.building_name || '-'}
                  </div>
                </Col>

                {/* Floor Name */}
                <Col span={8}>
                  <span style={{ fontWeight: 600 }}>Floor Name :</span>
                </Col>
                <Col span={16}>
                  <div
                    style={{
                      backgroundColor: '#ffffff',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    {selectedFloor?.wing_name}
                  </div>
                </Col>
              </Row>
            </div>
          </Modal>

        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default FloorMaster
